import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type ChatRole = 'user' | 'assistant' | 'system';

export interface ChatMessageItem {
  id?: string; // server id if available (stringified)
  clientId: string; // local client id - stable until reconciliation
  conversationRef: string; // public_id for the conversation
  role: ChatRole;
  content: string;
  createdAt: string; // ISO string
  status: 'pending' | 'saving' | 'saved' | 'error';
  isDraft?: boolean;
  seq?: number; // Monotonic sequence counter for stable ordering
}

export interface PaginationState {
  hasMore: boolean;
  nextBeforeId?: number; // earliest message_id cursor for pagination
}

export type HydrationState = 'idle' | 'loading' | 'ready';

export interface CurrentStreamState {
  requestId: string;
  draftClientId: string;
  conversationRef: string;
  // Optional: when streaming against a specific thread (branched chat)
  threadId?: string;
  parentMessageId?: string | null;
  // Optional: when the user message should not be added to the main lane, but
  // we still need to stream using this text (e.g., split side composer)
  pendingUserText?: string;
  // Optional: marks a side-stream (split UI-only) so FE should not reconcile
  // or hydrate the main lane after stream completion.
  sideStream?: boolean;
  // Optional attachments for this turn (default ON; kill-switch handled at Worker)
  attachments?: Array<{ documentId: string; mimeType?: string; originalFilename?: string; size?: number }>;
  status: 'idle' | 'streaming' | 'complete' | 'error';
  error?: string;
}

export interface ChatStoreState {
  // Conversations
  activeConvRef: string | null;
  hydration: Record<string, HydrationState>;
  pagination: Record<string, PaginationState>;

  // Messages (normalized)
  messagesById: Record<string, ChatMessageItem>;
  messageIdsByConvRef: Record<string, string[]>; // ordered ascending by createdAt
  nextSeq: number; // Global monotonic sequence counter

  // Streaming (per active conversation)
  currentStream?: CurrentStreamState;
  connectionStatus: 'connecting' | 'connected' | 'error';

  // Actions
  setActiveConversation: (convRef: string | null) => void;
  setHydrationStatus: (convRef: string, status: HydrationState) => void;

  hydrateConversation: (
    convRef: string,
    messages: ChatMessageItem[],
    hasMore: boolean,
    nextBeforeId?: number
  ) => void;

  prependOlderMessages: (
    convRef: string,
    olderMessages: ChatMessageItem[],
    hasMore: boolean,
    nextBeforeId?: number
  ) => void;

  addUserMessageDraft: (convRef: string, text: string) => string; // returns clientId
  reconcileMessageId: (clientId: string, serverId: string) => void;

  // Start a stream for a conversation, optionally targeting a specific thread.
  // When sideStream is true, the assistant draft is NOT added to the main lane's
  // messageIds (so the left lane won't render it). We still keep it in messagesById
  // so SSE can append tokens and the split panel can mirror content.
  startStream: (convRef: string, opts?: { threadId?: string; parentMessageId?: string | null; sideStream?: boolean; pendingUserText?: string; attachments?: Array<{ documentId: string; mimeType?: string; originalFilename?: string; size?: number }> }) => string; // returns draftAssistantClientId
  appendToStream: (token: string) => void;
  completeStream: () => void;
  setStreamError: (error: string) => void;
  resetStream: () => void;

  // Remove a message by its clientId or server id from store
  removeMessageByClientId: (clientIdOrId: string) => void;

  // Utilities
  upsertMessages: (convRef: string, incoming: ChatMessageItem[]) => void;
  clearConversation: (convRef: string) => void;
  clearAllMessages: () => void;
}

function generateClientId(prefix: string = 'msg'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Normalize content for stable comparison (collapse whitespace and trim)
function normalizeForCompare(s?: string): string {
  return (s || '').replace(/\s+/g, ' ').trim();
}

// Compute a createdAt that is strictly after the last message in a conversation.
// Guards against clock drift and millisecond collisions to maintain visual order.
// IMPORTANT: Ensure minimum spacing of 10ms between messages for stable ordering
function computeSafeCreatedAt(
  convRef: string,
  messagesById: Record<string, ChatMessageItem>,
  messageIdsByConvRef: Record<string, string[]>,
  offsetMs: number = 0
): string {
  const now = Date.now() + Math.max(0, offsetMs);
  const ids = messageIdsByConvRef[convRef] || [];
  let lastTs = -Infinity;
  
  // Find the timestamp of the last message
  for (let i = ids.length - 1; i >= 0; i--) {
    const m = messagesById[ids[i]];
    if (!m) continue;
    const t = new Date(m.createdAt).getTime();
    if (Number.isFinite(t)) { 
      lastTs = Math.max(lastTs, t); 
      break; 
    }
  }
  
  // Ensure at least 10ms spacing from the last message for stable ordering
  const MIN_SPACING_MS = 10;
  const safeTs = Number.isFinite(lastTs) 
    ? Math.max(now, lastTs + MIN_SPACING_MS) 
    : now;
    
  return new Date(safeTs).toISOString();
}

function sortMessageIds(
  ids: string[],
  messagesById: Record<string, ChatMessageItem>
): string[] {
  const sorted = ids
    .slice()
    .sort((a, b) => {
      const ma = messagesById[a];
      const mb = messagesById[b];
      if (!ma && !mb) return 0;
      if (!ma) return -1;
      if (!mb) return 1;

      // Primary: if both have seq, sort deterministically by seq
      const aHasSeq = typeof ma.seq === 'number' && Number.isFinite(ma.seq);
      const bHasSeq = typeof mb.seq === 'number' && Number.isFinite(mb.seq);
      if (aHasSeq && bHasSeq) {
        const seqDiff = (ma.seq as number) - (mb.seq as number);
        if (seqDiff !== 0) return seqDiff;
      } else if (aHasSeq !== bHasSeq) {
        // If only one has seq, prefer the one with seq to maintain insertion order
        return aHasSeq ? -1 : 1;
      }

      // Secondary: sort by timestamp, handling invalid dates robustly
      const taRaw = new Date(ma.createdAt).getTime();
      const tbRaw = new Date(mb.createdAt).getTime();
      const ta = Number.isFinite(taRaw) ? taRaw : Number.POSITIVE_INFINITY;
      const tb = Number.isFinite(tbRaw) ? tbRaw : Number.POSITIVE_INFINITY;
      if (ta !== tb) return ta - tb;

      // Tertiary: enforce user-before-assistant to avoid visual flip on ties
      const roleRank = (role?: ChatRole) => {
        switch (role) {
          case 'user': return 0;
          case 'assistant': return 1;
          case 'system': return 2;
          default: return 3;
        }
      };
      const rr = roleRank(ma.role) - roleRank(mb.role);
      if (rr !== 0) return rr;

      // Stable fallback by id/clientId
      const fa = ma.id || ma.clientId;
      const fb = mb.id || mb.clientId;
      return fa.localeCompare(fb);
    });

  return sorted;
}

export const useChatStore = create<ChatStoreState>()(
  devtools((set, get) => ({
    activeConvRef: null,
    hydration: {},
    pagination: {},
    messagesById: {},
    messageIdsByConvRef: {},
    nextSeq: 1,
    currentStream: undefined,
    connectionStatus: 'connected',

    setActiveConversation: (convRef) => {
      set({ activeConvRef: convRef });
      try {
        console.log('[ChatStore] setActiveConversation', { convRef });
      } catch {}
    },

    setHydrationStatus: (convRef, status) => {
      set((state) => ({
        hydration: { ...state.hydration, [convRef]: status },
      }));
      try {
        console.log('[ChatStore] setHydrationStatus', { convRef, status });
      } catch {}
    },

    hydrateConversation: (convRef, messages, hasMore, nextBeforeId) => {
      const state = get();
      // Merge incoming with existing; never drop existing (e.g., in-progress drafts)
      const messagesById = { ...state.messagesById };
      const idSet = new Set<string>(state.messageIdsByConvRef[convRef] || []);
      let nextSeq = state.nextSeq;
      
      // Build a map of existing messages by their server ID for matching
      const existingByServerId = new Map<string, ChatMessageItem>();
      for (const id of idSet) {
        const msg = messagesById[id];
        if (msg?.id) {
          existingByServerId.set(msg.id, msg);
        }
      }
      
      for (const msg of messages) {
        const serverKey = (msg.id || '').toString();
        // If this is a server message and we have a recent matching draft, reconcile proactively
        if (serverKey) {
          // Find a recent draft in this conversation that matches role+content
          const existingIdsAsc = state.messageIdsByConvRef[convRef] || [];
          let matchedDraftId: string | null = null;
          for (let i = existingIdsAsc.length - 1; i >= 0; i--) {
            const did = existingIdsAsc[i];
            const dmsg = messagesById[did];
            if (!dmsg) continue;
            if (dmsg.conversationRef !== convRef) continue;
            if (dmsg.role !== (msg as any).role) continue;
            // Allow reconciling either a draft or a just-completed assistant message
            // that is client-only (no server id yet) and in pending/saving state.
            const isClientOnly = !dmsg.id; // no server id yet
            const isReconcilable = (
              !!dmsg.isDraft ||
              (isClientOnly && (dmsg.status === 'pending' || dmsg.status === 'saving' || dmsg.status === 'saved'))
            );
            if (!isReconcilable) continue;
            // Normalize content to handle minor whitespace/newline drift
            const draftNorm = normalizeForCompare(dmsg.content);
            const serverNorm = normalizeForCompare((msg as any).content as any);
            if (draftNorm && draftNorm === serverNorm) {
              matchedDraftId = did;
              break;
            }
          }
          if (matchedDraftId) {
            const draft = messagesById[matchedDraftId]!;
            // Upgrade draft to server message, preserving createdAt/seq and removing the draft id
            const upgraded: ChatMessageItem = {
              ...draft,
              ...msg,
              id: serverKey,
              isDraft: false,
              status: 'saved',
              createdAt: draft.createdAt,
              seq: draft.seq,
              content: draft.content || (msg as any).content || ''
            };
            delete messagesById[matchedDraftId];
            messagesById[serverKey] = upgraded;
            idSet.delete(matchedDraftId);
            idSet.add(serverKey);
            continue; // handled this message
          }
        }

        // Default path: merge by key (server id preferred)
        const key = (msg.id || msg.clientId) as string;
        const existing = messagesById[key] || (serverKey ? existingByServerId.get(serverKey) : undefined);

        if (existing?.seq !== undefined) {
          // Preserve sequence if present
          messagesById[key] = { ...messagesById[key], ...msg, seq: existing.seq } as any;
        } else {
          // Assign a monotonic sequence to new or sequence-less messages for stable ordering
          messagesById[key] = { ...messagesById[key], ...msg, seq: (messagesById[key]?.seq ?? nextSeq++) } as any;
        }
        idSet.add(key);
      }

      const mergedIds = Array.from(idSet);
      const orderedIds = sortMessageIds(mergedIds, messagesById);

      set({
        messagesById,
        messageIdsByConvRef: {
          ...state.messageIdsByConvRef,
          [convRef]: orderedIds,
        },
        hydration: { ...state.hydration, [convRef]: 'ready' },
        pagination: {
          ...state.pagination,
          [convRef]: { hasMore, nextBeforeId },
        },
        nextSeq,
      });

      try {
        console.log('[ChatStore] hydrateConversation', {
          convRef,
          incomingCount: messages.length,
          existingCount: (state.messageIdsByConvRef[convRef] || []).length,
          mergedCount: orderedIds.length,
          hasMore,
          nextBeforeId,
        });
      } catch {}
    },

    prependOlderMessages: (convRef, olderMessages, hasMore, nextBeforeId) => {
      const state = get();
      const messagesById = { ...state.messagesById };
      const existingIds = new Set(state.messageIdsByConvRef[convRef] || []);
      const newIds: string[] = [];
      let nextSeq = state.nextSeq;
      for (const msg of olderMessages) {
        const key = msg.id || msg.clientId;
        const existing = messagesById[key];
        const hasSeq = existing?.seq !== undefined || msg.seq !== undefined;
        messagesById[key] = { ...existing, ...msg, seq: hasSeq ? (msg.seq ?? existing?.seq) : nextSeq++ } as any;
        if (!existingIds.has(key)) newIds.push(key);
      }
      const combinedIds = [...newIds, ...(state.messageIdsByConvRef[convRef] || [])];
      const orderedIds = sortMessageIds(combinedIds, messagesById);

      set({
        messagesById,
        messageIdsByConvRef: { ...state.messageIdsByConvRef, [convRef]: orderedIds },
        pagination: { ...state.pagination, [convRef]: { hasMore, nextBeforeId } },
        nextSeq,
      });

      try {
        console.log('[ChatStore] prependOlderMessages', {
          convRef,
          added: olderMessages.length,
          newTotal: orderedIds.length,
          hasMore,
          nextBeforeId,
        });
      } catch {}
    },

    addUserMessageDraft: (convRef, text) => {
      const clientId = generateClientId('user');
      // Ensure user message timestamps are strictly increasing vs. prior messages
      const stateForTs = get();
      // Use 0ms offset for user messages (they come first)
      const createdAt = computeSafeCreatedAt(convRef, stateForTs.messagesById, stateForTs.messageIdsByConvRef, 0);
      const seq = stateForTs.nextSeq;
      const draft: ChatMessageItem = {
        clientId,
        conversationRef: convRef,
        role: 'user',
        content: text,
        createdAt,
        status: 'pending',
        // Mark as draft so hydrateConversation can reconcile with
        // the server-saved message (especially for branching chats)
        isDraft: true,
        seq,
      };
      set((state) => {
        const messagesById = { ...state.messagesById, [clientId]: draft };
        // Initialize conversation array if it doesn't exist
        const existingIds = state.messageIdsByConvRef[convRef] || [];
        const ids = [...existingIds, clientId];
        return {
          messagesById,
          messageIdsByConvRef: {
            ...state.messageIdsByConvRef,
            [convRef]: sortMessageIds(ids, messagesById),
          },
          nextSeq: state.nextSeq + 1,
        };
      });
      try {
        console.log('[ChatStore] addUserMessageDraft', {
          convRef,
          clientId,
          contentLength: text.length,
          seq,
        });
      } catch {}
      return clientId;
    },

    reconcileMessageId: (clientId, serverId) => {
      set((state) => {
        const existing = state.messagesById[clientId];
        if (!existing) {
          console.warn('[ChatStore] reconcileMessageId: Message not found', { clientId, serverId });
          return {} as Partial<ChatStoreState>;
        }
        const serverKey = serverId.toString();
        // Preserve the original createdAt timestamp and seq to maintain ordering
        const updated: ChatMessageItem = { 
          ...existing, 
          id: serverKey, 
          status: 'saved',
          // Keep the original createdAt and seq to preserve message ordering
          createdAt: existing.createdAt,
          seq: existing.seq
        };
        const messagesById = { ...state.messagesById };
        delete messagesById[clientId];
        messagesById[serverKey] = updated;
        
        // Replace clientId with serverId in the ordered list
        const rawIds = state.messageIdsByConvRef[existing.conversationRef] || [];
        const idsMapped = rawIds.map((id) => (id === clientId ? serverKey : id));
        // Deduplicate potential duplicate serverKey entries caused by hydration + reconcile races
        const seen = new Set<string>();
        const ids = idsMapped.filter((id) => {
          if (!seen.has(id)) { seen.add(id); return true; }
          return false;
        });
        
        // Re-sort to ensure proper ordering is maintained
        const sortedIds = sortMessageIds(ids, messagesById);
        
        return {
          messagesById,
          messageIdsByConvRef: {
            ...state.messageIdsByConvRef,
            [existing.conversationRef]: sortedIds,
          },
        };
      });
      try {
        console.log('[ChatStore] reconcileMessageId', { clientId, serverId });
      } catch {}
    },

    startStream: (convRef, opts) => {
      console.log('[ChatStore] 🚀 START STREAM CALLED:', {
        conversationRef: convRef,
        timestamp: new Date().toISOString()
      });

      const draftClientId = generateClientId('asst');
      // Ensure assistant draft sorts after the last message (usually the user draft)
      // With seq-first ordering, we can avoid adding artificial delay here
      const stateForTs = get();
      const createdAt = computeSafeCreatedAt(convRef, stateForTs.messagesById, stateForTs.messageIdsByConvRef, 0);
      const requestId = `SSE-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const seq = stateForTs.nextSeq;
      const draft: ChatMessageItem = {
        clientId: draftClientId,
        conversationRef: convRef,
        role: 'assistant',
        content: '',
        createdAt,
        status: 'pending',
        isDraft: true,
        seq,
      };

      console.log('[ChatStore] 📝 Creating assistant draft:', {
        draftClientId,
        requestId,
        conversationRef: convRef,
        createdAt,
        seq
      });

      set((state) => {
        const messagesById = { ...state.messagesById, [draftClientId]: draft };
        const ids = [...(state.messageIdsByConvRef[convRef] || [])];
        // Only show assistant draft in main lane when NOT a side stream
        if (!opts?.sideStream) ids.push(draftClientId);
        const currentMessages = state.messageIdsByConvRef[convRef] || [];
        
        console.log('[ChatStore] 📊 Stream state update:', {
          existingMessageCount: currentMessages.length,
          newMessageCount: ids.length,
          streamStatus: 'streaming'
        });

        return {
          messagesById,
          messageIdsByConvRef: {
            ...state.messageIdsByConvRef,
            [convRef]: sortMessageIds(ids, messagesById),
          },
          nextSeq: state.nextSeq + 1,
          currentStream: { requestId, draftClientId, conversationRef: convRef, status: 'streaming', threadId: opts?.threadId, parentMessageId: opts?.parentMessageId ?? null, pendingUserText: opts?.pendingUserText, sideStream: !!opts?.sideStream, attachments: (opts?.attachments && Array.isArray(opts.attachments)) ? opts.attachments.slice(0,3) : undefined },
          connectionStatus: 'connecting',
        };
      });

      console.log('[ChatStore] ✅ START STREAM COMPLETE:', { 
        convRef, 
        draftClientId, 
        requestId,
        timestamp: new Date().toISOString()
      });

      return draftClientId;
    },

    appendToStream: (token) => {
      const VERBOSE_CHAT_LOGS = (() => {
        try {
          const v = (import.meta as any)?.env?.VITE_CHAT_VERBOSE_LOGS;
          return typeof v === 'string' && (v === '1' || v.toLowerCase() === 'true');
        } catch { return false; }
      })();
      if (VERBOSE_CHAT_LOGS) {
        console.log('[ChatStore] 🎯 appendToStream CALLED:', {
          tokenLength: token.length,
          tokenPreview: token.substring(0, 30),
          timestamp: new Date().toISOString()
        });
      }

      set((state) => {
        const draftClientId = state.currentStream?.draftClientId;
        if (VERBOSE_CHAT_LOGS) {
          console.log('[ChatStore] 📝 Current stream state:', {
            hasDraftClientId: !!draftClientId,
            draftClientId,
            streamStatus: state.currentStream?.status
          });
        }

        if (!draftClientId) {
          console.error('[ChatStore] ❌ NO DRAFT CLIENT ID - Cannot append!');
          return {} as Partial<ChatStoreState>;
        }
        
        const draft = state.messagesById[draftClientId];
        if (VERBOSE_CHAT_LOGS) {
          console.log('[ChatStore] 📄 Draft message state:', {
            hasDraft: !!draft,
            currentLength: draft?.content?.length || 0,
            isDraft: draft?.isDraft,
            role: draft?.role
          });
        }

        if (!draft) {
          console.error('[ChatStore] ❌ NO DRAFT MESSAGE - Cannot append!');
          return {} as Partial<ChatStoreState>;
        }
        // Normalize incoming chunk and apply snapshot-aware merge
        const existing = draft.content || '';
        // Normalize CRLF/CR to LF and strip leading solitary CRs
        let incoming = (token || '').replace(/\r\n/g, '\n').replace(/\r/g, '');
        // Heuristic: if incoming looks like a full snapshot (starts with existing or with optional leading newlines)
        const incomingStripped = incoming.replace(/^\n+/, '');
        let newContent: string;
        if (incomingStripped.startsWith(existing) && incomingStripped.length >= existing.length) {
          // Treat as replacement-snapshot (e.g., AI SDK-style frames sending the whole buffer each time)
          newContent = incomingStripped;
          if (VERBOSE_CHAT_LOGS) {
            console.log('[ChatStore] 📷 Snapshot detected; replacing draft content', {
              existingLen: existing.length,
              incomingLen: incoming.length,
            });
          }
        } else if (existing.endsWith(incoming)) {
          // Likely duplicate append; skip to avoid doubled content
          if (VERBOSE_CHAT_LOGS) {
            console.log('[ChatStore] 🔁 Duplicate tail detected; skipping append');
          }
          return {} as Partial<ChatStoreState>;
        } else {
          newContent = existing + incoming;
        }
        if (VERBOSE_CHAT_LOGS) {
          console.log('[ChatStore] ✅ APPENDING TOKEN:', {
            draftClientId,
            appendedLen: incoming.length,
            totalLen: newContent.length,
            newContentPreview: newContent.substring(newContent.length - 50),
            timestamp: new Date().toISOString()
          });
        }

        return {
          messagesById: {
            ...state.messagesById,
            [draftClientId]: { ...draft, content: newContent },
          },
          connectionStatus: 'connected',
        };
      });
      
      if (VERBOSE_CHAT_LOGS) {
        console.log('[ChatStore] ✅ appendToStream COMPLETE');
      }
    },

    completeStream: () => {
      set((state) => {
        const draftClientId = state.currentStream?.draftClientId;
        if (!draftClientId) return {} as Partial<ChatStoreState>;
        const draft = state.messagesById[draftClientId];
        if (!draft) return {} as Partial<ChatStoreState>;
        try {
          console.log('[ChatStore] completeStream', { draftClientId, finalLen: (draft.content || '').length });
        } catch {}
        return {
          messagesById: {
            ...state.messagesById,
            // Optimistically mark as saved to avoid long "finishing" delays;
            // reconcile later will upgrade to server id when available.
            [draftClientId]: { ...draft, isDraft: false, status: 'saved' },
          },
          currentStream: state.currentStream
            ? { ...state.currentStream, status: 'complete' }
            : undefined,
          connectionStatus: 'connected',
        };
      });
    },

    setStreamError: (error) => {
      set((state) => ({
        currentStream: state.currentStream
          ? { ...state.currentStream, status: 'error', error }
          : { requestId: `SSE-${Date.now()}`, draftClientId: '', conversationRef: '', status: 'error', error },
        connectionStatus: 'error',
      }));
      try {
        console.error('[ChatStore] failStream', { error });
      } catch {}
    },

    resetStream: () => {
      set({ currentStream: undefined });
      try {
        console.log('[ChatStore] resetStream');
      } catch {}
    },

    removeMessageByClientId: (clientIdOrId) => {
      set((state) => {
        const messagesById = { ...state.messagesById };
        const target = messagesById[clientIdOrId];
        // If not found by key, search for a message whose id or clientId matches
        let convRef: string | null = null;
        if (target) {
          convRef = target.conversationRef;
          delete messagesById[clientIdOrId];
        } else {
          // Scan to find matching by clientId or id
          for (const [k, v] of Object.entries(messagesById)) {
            if (!v) continue;
            if (k === clientIdOrId || v.clientId === clientIdOrId || v.id === clientIdOrId) {
              convRef = v.conversationRef;
              delete messagesById[k];
              break;
            }
          }
        }
        if (!convRef) return {} as Partial<ChatStoreState>;
        const existingIds = state.messageIdsByConvRef[convRef] || [];
        const filtered = existingIds.filter((id) => id !== clientIdOrId);
        return {
          messagesById,
          messageIdsByConvRef: { ...state.messageIdsByConvRef, [convRef]: filtered },
        } as Partial<ChatStoreState>;
      });
      try {
        console.log('[ChatStore] removeMessageByClientId', { clientIdOrId });
      } catch {}
    },

    upsertMessages: (convRef, incoming) => {
      const state = get();
      const messagesById = { ...state.messagesById };
      const idSet = new Set(state.messageIdsByConvRef[convRef] || []);
      let nextSeq = state.nextSeq;
      for (const msg of incoming) {
        const key = msg.id || msg.clientId;
        const existing = messagesById[key];
        const hasSeq = existing?.seq !== undefined || msg.seq !== undefined;
        messagesById[key] = {
          ...existing,
          ...msg,
          // Assign a seq if none exists to keep deterministic ordering
          seq: hasSeq ? (msg.seq ?? existing?.seq) : nextSeq++,
        } as any;
        idSet.add(key);
      }
      const ids = Array.from(idSet);
      const orderedIds = sortMessageIds(ids, messagesById);
      set({
        messagesById,
        messageIdsByConvRef: { ...state.messageIdsByConvRef, [convRef]: orderedIds },
        nextSeq,
      });
      try {
        console.log('[ChatStore] upsertMessages', {
          convRef,
          incomingCount: incoming.length,
          newTotal: orderedIds.length,
        });
      } catch {}
    },

    clearConversation: (convRef) => {
      console.log('[ChatStore] 🗑️ Clearing conversation:', convRef);
      set((state) => {
        // Remove message IDs for this conversation
        const newMessageIdsByConvRef = { ...state.messageIdsByConvRef };
        const messageIdsToRemove = newMessageIdsByConvRef[convRef] || [];
        delete newMessageIdsByConvRef[convRef];
        
        // Remove the actual messages
        const newMessagesById = { ...state.messagesById };
        messageIdsToRemove.forEach(id => {
          delete newMessagesById[id];
        });
        
        // Clear hydration and pagination for this conversation
        const newHydration = { ...state.hydration };
        delete newHydration[convRef];
        
        const newPagination = { ...state.pagination };
        delete newPagination[convRef];
        
        return {
          messageIdsByConvRef: newMessageIdsByConvRef,
          messagesById: newMessagesById,
          hydration: newHydration,
          pagination: newPagination,
        };
      });
    },

    clearAllMessages: () => {
      console.log('[ChatStore] 🗑️ Clearing ALL messages');
      set({
        messagesById: {},
        messageIdsByConvRef: {},
        hydration: {},
        pagination: {},
        currentStream: undefined,
        activeConvRef: null,
      });
    },
  }))
);

// Selectors
export const selectActiveConvRef = (s: ChatStoreState) => s.activeConvRef;
export const selectMessagesForConversation = (convRef: string) => (s: ChatStoreState): ChatMessageItem[] => {
  const ids = s.messageIdsByConvRef[convRef] || [];
  return ids.map((id) => s.messagesById[id]).filter(Boolean);
};
export const selectHydration = (convRef: string) => (s: ChatStoreState) => s.hydration[convRef] || 'idle';
// Stable default to avoid referential changes triggering rerenders
const DEFAULT_PAGINATION: PaginationState = Object.freeze({ hasMore: false });
export const selectPagination = (convRef: string) => (s: ChatStoreState) => s.pagination[convRef] || DEFAULT_PAGINATION;
export const selectConnectionStatus = (s: ChatStoreState) => s.connectionStatus;
export const selectCurrentStream = (s: ChatStoreState) => s.currentStream;
